"use client";

/**
 * Auth Context Hook
 *
 * Abstraction layer for Supabase authentication hooks.
 * Components should use this instead of directly importing from Supabase.
 *
 * Architecture Rule: AU-01 - Auth Abstraction Layer
 *
 * Migration Note: This file previously used Clerk. Now uses Supabase Auth.
 * The public API remains the same for backward compatibility.
 */
import { useSupabaseAuth } from "@/components/auth/supabase-auth-provider";

// ============================================================================
// Types - Maintaining backward compatibility with Clerk types
// ============================================================================

export interface AuthContext {
  // Loading states
  isLoaded: boolean;

  // Authentication state
  isSignedIn: boolean;
  userId: string | null | undefined;
  sessionId: string | null | undefined;

  // User data
  user: {
    id: string;
    primaryEmailAddress?: { emailAddress: string };
    firstName?: string | null;
    lastName?: string | null;
    fullName?: string | null;
    imageUrl?: string;
  } | null;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  fullName: string | null | undefined;
  email: string | null | undefined;
  imageUrl: string | undefined;

  // Organization data
  organizations: Array<{ id: string; name: string; slug: string; type: string; role: string }>;
  activeOrganization: { id: string; name: string; slug: string; type: string; role: string } | null;

  // Auth actions
  signOut: () => Promise<void>;
  setActive: (params: { session?: string | null; organization?: string | null }) => Promise<void>;

  // Sign in state (for sign-in flows)
  signIn: {
    create: (params: {
      identifier: string;
      password: string;
    }) => Promise<{ status: string; createdSessionId?: string }>;
  } | null;
  signInLoaded: boolean;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Unified auth context hook that wraps Supabase authentication.
 *
 * Usage:
 * ```tsx
 * const { isSignedIn, user, signOut, setActive } = useAuthContext();
 * ```
 */
export function useAuthContext(): AuthContext {
  const auth = useSupabaseAuth();

  // Parse name parts from full name if available
  const nameParts = auth.user?.name?.split(" ") ?? [];
  const firstName = nameParts[0] ?? null;
  const lastName = nameParts.slice(1).join(" ") || null;

  // Create a compatible user object
  const user = auth.user
    ? {
        id: auth.user.id,
        primaryEmailAddress: { emailAddress: auth.user.email },
        firstName,
        lastName,
        fullName: auth.user.name,
        imageUrl: undefined, // Supabase doesn't have built-in avatars
      }
    : null;

  // Create a compatible signIn object for demo login compatibility
  const signIn = auth.isLoaded
    ? {
        create: async (params: { identifier: string; password: string }) => {
          const result = await auth.signIn(params.identifier, params.password);
          if (result.error) {
            throw result.error;
          }
          return {
            status: "complete",
            createdSessionId: auth.session?.access_token,
          };
        },
      }
    : null;

  return {
    // Loading states
    isLoaded: auth.isLoaded,

    // Authentication state
    isSignedIn: auth.isSignedIn,
    userId: auth.userId,
    sessionId: auth.session?.access_token ?? null,

    // User data
    user,
    firstName,
    lastName,
    fullName: auth.user?.name ?? null,
    email: auth.user?.email ?? null,
    imageUrl: undefined,

    // Organization data
    organizations: auth.organizations,
    activeOrganization: auth.activeOrganization,

    // Auth actions
    signOut: auth.signOut,
    setActive: async (params) => {
      if (params.organization) {
        await auth.setActiveOrganization(params.organization);
      }
    },

    // Sign in state
    signIn,
    signInLoaded: auth.isLoaded,
  };
}

/**
 * Hook for checking if user is authenticated
 * Simpler alternative when you only need auth status
 */
export function useIsAuthenticated(): { isLoaded: boolean; isSignedIn: boolean } {
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  return { isLoaded, isSignedIn };
}

/**
 * Hook for getting basic user info
 * Use when you only need user data, not auth actions
 */
export function useCurrentUser(): {
  isLoaded: boolean;
  user: AuthContext["user"];
  userId: string | null | undefined;
} {
  const auth = useSupabaseAuth();

  const nameParts = auth.user?.name?.split(" ") ?? [];
  const firstName = nameParts[0] ?? null;
  const lastName = nameParts.slice(1).join(" ") || null;

  const user = auth.user
    ? {
        id: auth.user.id,
        primaryEmailAddress: { emailAddress: auth.user.email },
        firstName,
        lastName,
        fullName: auth.user.name,
        imageUrl: undefined,
      }
    : null;

  return { isLoaded: auth.isLoaded, user, userId: auth.userId };
}

/**
 * Hook for sign-in flow
 * Use in sign-in pages and demo login
 */
export function useSignInFlow(): {
  signIn: AuthContext["signIn"];
  isLoaded: boolean;
  setActive: (params: { session?: string | null; organization?: string | null }) => Promise<void>;
} {
  const auth = useSupabaseAuth();

  const signIn = auth.isLoaded
    ? {
        create: async (params: { identifier: string; password: string }) => {
          const result = await auth.signIn(params.identifier, params.password);
          if (result.error) {
            throw result.error;
          }
          return {
            status: "complete",
            createdSessionId: auth.session?.access_token,
          };
        },
      }
    : null;

  return {
    signIn,
    isLoaded: auth.isLoaded,
    setActive: async (params) => {
      if (params.organization) {
        await auth.setActiveOrganization(params.organization);
      }
    },
  };
}
